<template>
  <div v-if="visible" class="client-class-manager-overlay" @click.self="close">
    <div class="client-class-manager card p-4">
      <div class="d-flex justify-content-between align-items-start mb-3">
        <div>
          <h5 class="mb-1">{{ $t('manageClientClasses') }}</h5>
          <strong>{{ client.name }}</strong>
          <small class="d-block text-secondary">{{ client.email }}</small>
        </div>
        <md-button class="md-simple md-just-icon" @click="close">
          <md-icon>close</md-icon>
        </md-button>
      </div>

      <div class="loading-container text-center p-4" v-if="isLoading">
        <md-progress-spinner :md-diameter="50" md-mode="indeterminate" />
      </div>

      <template v-else>
        <div v-if="!canEnrollInClasses" class="alert alert-warning mb-3">
          <small>{{ inactiveMessage }}</small>
        </div>

        <div class="quota-summary p-3 mb-3">
          <div v-if="quota.cupos === -1">
            <md-icon>all_inclusive</md-icon> {{ $t('unlimitedQuota') }}
          </div>
          <div v-else>
            <div><strong>{{ $t('classQuotaPeriod') }}:</strong> {{ quota.classesAttendedCount }} / {{ quota.cupos || 0 }} {{ $t('classesUsed') }}</div>
            <div class="mt-1">
              <span class="badge" :class="quota.remainingCupos > 0 ? 'badge-success' : 'badge-danger'">
                {{ quota.remainingCupos }} {{ $t('availableSlots') }}
              </span>
            </div>
          </div>
          <small v-if="quota.paymentType === 'month' && quota.paymentDate" class="text-secondary d-block mt-2">
            {{ $t('paymentPeriodUntil') }}: {{ formatPaymentDate(quota.paymentDate) }}
          </small>
        </div>

        <h6>{{ $t('enrolledClassesInPeriod') }} ({{ enrollments.length }})</h6>
        <p v-if="enrollments.length === 0" class="text-secondary">{{ $t('notEnrolledInAnyClass') }}</p>
        <div v-for="act in enrollments" :key="act._id" class="enrollment-row mt-2 p-2">
          <strong>{{ act.name }}</strong>
          <small class="d-block text-secondary">
            <span v-if="act.startDate">{{ formatClassDate(act) }}</span>
            <span v-else>{{ daysOfWeek[act.day] }} {{ act.startTime }}hs</span>
            <span v-if="act.consumequota" class="ml-2">· {{ $t('consumesQuota') }}</span>
            <span v-else class="ml-2">· {{ $t('doesNotConsumeQuota') }}</span>
          </small>
          <md-button
            class="md-btn md-danger md-sm mt-1"
            :disabled="actionLoading === act._id"
            @click="removeEnrollment(act)"
          >
            {{ $t('unsubscribe') }}
          </md-button>
        </div>

        <hr class="my-4" />

        <h6>{{ $t('addClassToClient') }}</h6>
        <div class="md-autocomplete activity-user-search">
          <md-autocomplete
            v-model="activitySearch"
            :md-options="activitySearchOptions"
            :md-open-on-focus="false"
            @md-selected="onActivitySelected"
          >
            <label>{{ $t('searchClassToAdd') }}</label>
          </md-autocomplete>
        </div>
        <p v-if="activitySearch.trim().length === 1" class="text-secondary mt-1 mb-0">
          <small>{{ $t('typeMinCharsToSearch') }}</small>
        </p>
        <p v-else-if="activitySearch.trim().length >= 2 && filteredActivities.length === 0" class="text-secondary mt-1 mb-0">
          <small>{{ $t('noClassesFound') }}</small>
        </p>

        <div v-if="selectedActivity" class="mt-3 p-3 enroll-selected-user">
          <strong>{{ selectedActivity.name }}</strong>
          <small class="d-block text-secondary">{{ formatClassDate(selectedActivity) }}</small>
          <md-button
            class="md-btn md-success md-sm mt-2"
            :disabled="!!actionLoading"
            @click="addEnrollment(selectedActivity)"
          >
            {{ $t('signUpForThisClass') }}
          </md-button>
        </div>

        <div
          v-for="act in filteredActivities"
          :key="'add-' + act._id"
          class="enrollment-row mt-2 p-2 enroll-search-result"
          @click="selectActivity(act)"
        >
          <strong>{{ act.name }}</strong>
          <small class="d-block text-secondary">{{ formatClassDate(act) }}</small>
          <md-button
            class="md-btn md-success md-sm mt-1"
            :disabled="!!actionLoading"
            @click.stop="addEnrollment(act)"
          >
            {{ $t('signUpForThisClass') }}
          </md-button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { format } from 'date-fns';
import { formatAbsoluteDateLabel } from '@/utils/dateUtils';
import Fuse from 'fuse.js';
import Swal from 'sweetalert2';
import { getUserClassEnrollmentsAction } from '@/pages/Users/actions';
import { getActivity, signUpClassAction, deleteClassAction } from '@/pages/Stablishments/actions';

export default {
  name: 'ClientClassManager',
  props: {
    visible: { type: Boolean, default: false },
    client: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      isLoading: false,
      actionLoading: null,
      canEnrollInClasses: true,
      isActiveClient: true,
      quota: { cupos: 0, remainingCupos: 0, classesAttendedCount: 0 },
      enrollments: [],
      allActivities: [],
      fuseActivities: null,
      activitySearch: '',
      selectedActivity: null,
      daysOfWeek: ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'],
    };
  },
  computed: {
    inactiveMessage() {
      if (!this.isActiveClient) {
        return this.$t('clientInactiveCannotEnroll');
      }
      return this.$t('paymentExpiredCannotEnroll');
    },
    activitySearchOptions() {
      return this.filteredActivities.map((a) => this.activityOptionLabel(a));
    },
    filteredActivities() {
      const term = (this.activitySearch || '').trim();
      if (term.length < 2 || !this.fuseActivities) return [];
      const enrolledIds = new Set(this.enrollments.map((e) => e._id));
      return this.fuseActivities
        .search(term)
        .map((r) => r.item)
        .filter((a) => !enrolledIds.has(a._id))
        .slice(0, 20);
    },
  },
  watch: {
    visible(val) {
      if (val && this.client && this.client._id) {
        this.load();
      }
    },
  },
  methods: {
    close() {
      this.$emit('close');
    },
    formatPaymentDate(date) {
      return formatAbsoluteDateLabel(date);
    },
    formatClassDate(act) {
      if (!act.startDate) {
        return `${this.daysOfWeek[act.day]} ${act.startTime}hs`;
      }
      const d = new Date(act.startDate);
      return `${format(d, 'd')} ${this.$t(format(d, 'MMMM'))} ${format(d, 'yyyy')} ${act.startTime || ''}hs`;
    },
    activityOptionLabel(act) {
      return `${act.name} — ${this.formatClassDate(act)}`;
    },
    findActivityByLabel(label) {
      return this.availableActivities().find((a) => this.activityOptionLabel(a) === label);
    },
    availableActivities() {
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      const enrolledIds = new Set(this.enrollments.map((e) => e._id));
      return this.allActivities.filter((a) => {
        if (!a.registration || a.cancelled) return false;
        if (!a.startDate) return false;
        if (enrolledIds.has(a._id)) return false;
        const classDate = new Date(a.startDate);
        return classDate >= now;
      });
    },
    initActivitiesFuse() {
      const list = this.availableActivities();
      this.fuseActivities = new Fuse(list, {
        keys: ['name', 'startTime'],
        threshold: 0.35,
        ignoreLocation: true,
      });
    },
    applyQuota(quota) {
      if (quota) {
        this.quota = {
          cupos: quota.cupos,
          remainingCupos: quota.remainingCupos,
          classesAttendedCount: quota.classesAttendedCount,
          paymentType: quota.paymentType,
          paymentDate: quota.paymentDate,
        };
      }
    },
    async load() {
      this.isLoading = true;
      this.activitySearch = '';
      this.selectedActivity = null;
      try {
        const [enrollData, actData] = await Promise.all([
          getUserClassEnrollmentsAction(this.client._id),
          getActivity(),
        ]);
        if (enrollData.ok) {
          this.quota = {
            cupos: enrollData.cupos,
            remainingCupos: enrollData.remainingCupos,
            classesAttendedCount: enrollData.classesAttendedCount,
            paymentType: enrollData.paymentType,
            paymentDate: enrollData.paymentDate,
          };
          this.canEnrollInClasses = enrollData.canEnrollInClasses !== false;
          this.isActiveClient = enrollData.isActiveClient === true;
          this.enrollments = enrollData.enrollments || [];
        }
        if (actData.ok) {
          this.allActivities = actData.activities || [];
          this.initActivitiesFuse();
        }
      } catch (e) {
        console.error(e);
        Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudieron cargar las clases' });
      }
      this.isLoading = false;
    },
    onActivitySelected(label) {
      this.selectedActivity = this.findActivityByLabel(label);
    },
    selectActivity(act) {
      this.selectedActivity = act;
      this.activitySearch = this.activityOptionLabel(act);
    },
    async removeEnrollment(act) {
      const classLabel = this.formatClassDate(act);
      const result = await Swal.fire({
        title: this.$t('confirmRemoveFromClassTitle'),
        text: this.$t('confirmRemoveFromClassText')
          .replace('{user}', this.client.name || '')
          .replace('{class}', `${act.name} (${classLabel})`),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#6c757d',
        confirmButtonText: this.$t('yesRemoveFromClass'),
        cancelButtonText: this.$t('cancel'),
      });
      if (!result.isConfirmed) {
        return;
      }

      this.actionLoading = act._id;
      try {
        const response = await deleteClassAction(act._id, this.client._id);
        if (response.ok) {
          this.applyQuota(response.quota);
          await this.load();
          Swal.fire({
            icon: 'success',
            title: this.$t('classRemoved'),
            html: this.quotaSummaryHtml(),
            timer: 2500,
            showConfirmButton: false,
          });
          this.$emit('updated', { userId: this.client._id, quota: this.quota });
        }
      } catch (e) {
        Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudo quitar la clase' });
      }
      this.actionLoading = null;
    },
    async addEnrollment(act) {
      if (!this.canEnrollInClasses) {
        Swal.fire({
          icon: 'warning',
          title: this.$t('inactiveClient'),
          text: this.inactiveMessage,
        });
        return;
      }
      if (!act.startDate) {
        Swal.fire({ icon: 'warning', title: 'Clase recurrente', text: this.$t('recurringActivitiesMessage') });
        return;
      }
      this.actionLoading = act._id;
      try {
        const day = new Date(act.startDate);
        const utcDate = new Date(Date.UTC(
          day.getFullYear(), day.getMonth(), day.getDate(),
          day.getHours(), day.getMinutes(), day.getSeconds()
        ));
        const response = await signUpClassAction({
          userId: this.client._id,
          activityId: act._id,
          day: utcDate.toISOString(),
        });
        if (response.ok) {
          this.applyQuota(response.quota);
          await this.load();
          Swal.fire({
            icon: 'success',
            title: this.$t('classAdded'),
            html: this.quotaSummaryHtml(),
            timer: 2500,
            showConfirmButton: false,
          });
          this.$emit('updated', { userId: this.client._id, quota: this.quota });
        } else if (response.message === 'paymentExpired') {
          Swal.fire({
            icon: 'warning',
            title: this.$t('paymentExpired'),
            text: this.$t('paymentExpiredCannotEnroll'),
          });
        } else if (response.message === 'noQuotaLeft') {
          this.applyQuota(response.quota);
          Swal.fire({
            icon: 'warning',
            title: this.$t('noQuotaLeft'),
            text: this.$t('noQuotaLeftThisMonth'),
          });
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: response.message || 'No se pudo inscribir',
          });
        }
      } catch (e) {
        Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudo agregar la clase' });
      }
      this.actionLoading = null;
    },
    quotaSummaryHtml() {
      if (this.quota.cupos === -1) {
        return `<small>${this.$t('unlimitedQuota')}</small>`;
      }
      return `<small>${this.quota.remainingCupos} ${this.$t('availableSlots')} (${this.quota.classesAttendedCount}/${this.quota.cupos || 0})</small>`;
    },
  },
};
</script>

<style scoped>
.client-class-manager-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}
.client-class-manager {
  width: 100%;
  max-width: 520px;
  max-height: 90vh;
  overflow-y: auto;
  background: #fff;
}
.quota-summary {
  background: #f5f8fa;
  border-radius: 8px;
}
.enrollment-row {
  border: 1px solid #e8e8e8;
  border-radius: 6px;
}
.enroll-search-result {
  cursor: pointer;
}
.enroll-search-result:hover {
  background: #f0f4f8;
}
</style>
